import React, { useCallback, useEffect, useMemo } from 'react';
import { Assert } from '@cotera/utilities';
import { Icon, Text, toast } from '@cotera/client/app/components/ui';
import {
  IconName,
  ModalItem,
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components';
import { classNames } from '@cotera/client/app/components/utils';
import { useAnalytics, useTextFieldTracking } from '@cotera/client/app/etc';
import { openSafeUrl } from '@cotera/client/app/etc/safe-url';
import { useKeyPress } from '@cotera/client/app/hooks/use-key-press';
import {
  FuzzyKey,
  useFuzzySearch,
} from '@cotera/client/app/hooks/use-fuzzy-search';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  KeyboardNavigableList,
  resolveListKey,
} from '../../components/ui/keyboard-navigable-list';
import { Input } from '../../components/ui/input';
import { displayKey } from '../../lib/utils';
import { ResourceNavigatorModel } from '@cotera/client/app/v1/resources/navigation';
import { useNavigator } from './navigator';
import { useActionsContext } from '../framework/context';
import { ActionsManager } from '../framework';
import {
  Action,
  ApplicableContext,
  ModelResource,
  ReadActionsContext,
} from '../framework/types';

function actionPriority(action: Action, ctx: ApplicableContext): number {
  return typeof action.getPriority === 'function'
    ? action.getPriority(ctx)
    : action.priority ?? 0;
}

/**
 * Hoisted rather than written inline at the call site: a fresh array literal
 * every render invalidates the memo that holds the Fuse index, so the index was
 * being torn down and rebuilt on each keystroke *and* each hover.
 *
 * A title hit outranks a description hit — descriptions are long and match
 * loosely, so unweighted they drown out the thing the user actually named.
 */
const ACTION_SEARCH_KEYS: FuzzyKey<Action>[] = [
  { name: 'title', weight: 3 },
  { name: 'description', weight: 1 },
];

const RESOURCE_SEARCH_KEYS: FuzzyKey<ModelResource>[] = [
  { name: 'name', weight: 3 },
  { name: 'description', weight: 1 },
];

/**
 * `ignoreLocation` is the one that matters: by default Fuse scores a match by
 * how early it appears, which buries a column name sitting at the end of a long
 * `dataset.column` label. The tighter threshold (Fuse defaults to 0.6) trims the
 * tail of near-unrelated results that padded out every query.
 */
const FUZZY_OPTIONS = { threshold: 0.3, ignoreLocation: true };

/**
 * Resources are one entry per dataset, per *column of every dataset*, and per
 * agent, so a real graph produces thousands of them. Rendering the lot into the
 * modal on open is what made Ctrl-K slow to appear.
 *
 * The cap is silent: typing filters the list, so the tail is reachable without
 * being rendered up front.
 */
const MAX_BROWSABLE_RESOURCES = 50;

/** Matches come back ranked, so the tail past this is never read. */
const MAX_MATCHED_RESOURCES = 100;

/**
 * Width is pinned, height is not.
 *
 * The desktop width used to be a *range* (`min-w-[33vw] max-w-[90vw]`) against a
 * `w-fit` panel, so the modal resized itself horizontally around whatever the
 * longest row happened to be — it visibly jumped as you typed. Min and max are
 * now the same value, which leaves the height free to animate on its own as the
 * result count changes.
 */
const PALETTE_MODAL_SIZE = {
  custom:
    'w-[92vw] min-w-[92vw] max-w-[92vw] max-h-[calc(100dvh-9rem)] md:w-[46rem] md:min-w-[46rem] md:max-w-[46rem] md:max-h-[90vh]',
};

/**
 * The one definition of the palette modal, shared by both entry points — the
 * Ctrl-K binding here and the sidebar button's `useOpenCommandPalette`. They
 * used to carry separate copies of the size config and disagreed on whether to
 * `push` or `replace`.
 *
 * `onClose` covers the dismissals the view never sees: a click on the overlay,
 * and the Escape the modal manager handles. Those closes previously emitted
 * nothing, so the funnel counted opens that apparently never ended.
 */
export const commandPaletteModalItem = (params: {
  context: ReadActionsContext;
  analytics: ReturnType<typeof useAnalytics>;
}): ModalItem => {
  let closeTracked = false;

  // Escape reaches the modal component *and* the modal manager, and each calls
  // `onClose`; the view meanwhile tracks its own closes before calling
  // `close()`. First one through wins, so an open yields exactly one close.
  const trackClose = (trigger: string) => {
    if (closeTracked) {
      return;
    }
    closeTracked = true;
    params.analytics.track('Command Palette Closed', { trigger });
  };

  return {
    verticalAlign: 'top',
    size: PALETTE_MODAL_SIZE,
    onClose: () => trackClose('dismissed'),
    view: () => (
      <ModalPageView>
        <CommandPaletteView
          context={params.context}
          analytics={params.analytics}
          onClosed={trackClose}
        />
      </ModalPageView>
    ),
  };
};

export function CommandPalette() {
  const isOpen = useModalManager((s) => s.open);
  const manager = useModalManager((s) => s.actions);
  const context = useActionsContext();
  const analytics = useAnalytics();

  const openPalette = React.useCallback(
    (trigger: 'keyboard' | 'event' | 'gesture') => {
      if (isOpen) {
        return;
      }

      analytics.track('Command Palette Opened', { trigger });
      manager.push(commandPaletteModalItem({ context, analytics }));
    },
    [analytics, context, isOpen, manager]
  );

  const handleKeyboardOpen = React.useCallback(
    (e: KeyboardEvent) => {
      if (isOpen) {
        return;
      }

      e.preventDefault();
      openPalette('keyboard');
    },
    [isOpen, openPalette]
  );

  useKeyPress(
    (e) => e.key === 'k' && (e.ctrlKey || e.metaKey),
    handleKeyboardOpen
  );

  const handleCommandPalette = React.useCallback(
    (e: Event) => {
      e.preventDefault();
      openPalette('event');
    },
    [openPalette]
  );

  useEffect(() => {
    window.addEventListener('open-command-palette', handleCommandPalette);

    return () => {
      window.removeEventListener('open-command-palette', handleCommandPalette);
    };
  }, [handleCommandPalette]);

  return null;
}

export const CommandPaletteView: React.FC<{
  context: ReadActionsContext;
  analytics?: ReturnType<typeof useAnalytics>;
  /** Supplied by {@link commandPaletteModalItem}, which de-duplicates closes. */
  onClosed?: (trigger: string) => void;
}> = ({ context, analytics, onClosed }) => {
  const navigator = useNavigator();
  const modalManager = useModalManager((s) => s.actions);
  const defaultAnalytics = useAnalytics();
  const analyticsClient = analytics ?? defaultAnalytics;
  const trackSearch = useTextFieldTracking('Command Palette Search');
  const listboxId = React.useId();

  const trackClose = React.useCallback(
    (trigger: string) => {
      if (onClosed !== undefined) {
        onClosed(trigger);
        return;
      }
      analyticsClient.track('Command Palette Closed', { trigger });
    },
    [analyticsClient, onClosed]
  );

  const { getAllActions, getAllInScopeModels } = context;
  const [search, setSearch] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const ctx = useMemo(() => ActionsManager.fromExisting(context), [context]);

  const visibleActions = React.useMemo(() => {
    return getAllActions()
      .reverse()
      .filter(
        (x) =>
          !x.discrete &&
          x.applicable(ctx) &&
          (!x.requiresInputArgs() || x.ask !== undefined)
      );
  }, [getAllActions, ctx]);

  // Actions go straight to the fuzzy matcher. They used to be pre-filtered on
  // `title.includes(search)` first, which meant Fuse could only ever re-rank a
  // set that already had to contain the query verbatim: no typo tolerance
  // ("chrt" missed "Chart"), no out-of-order words ("chat new" missed "New
  // Chat"), and the `description` key was dead weight because a
  // description-only match had been dropped a step earlier.
  const doSearch = useFuzzySearch(
    visibleActions,
    ACTION_SEARCH_KEYS,
    FUZZY_OPTIONS
  );

  const allResources = React.useMemo(() => {
    return getAllInScopeModels().flatMap((x) => x.resources?.() ?? []);
  }, [getAllInScopeModels]);

  const doResourceSearch = useFuzzySearch(
    allResources,
    RESOURCE_SEARCH_KEYS,
    FUZZY_OPTIONS
  );

  // Memoized, not computed inline. As a bare expression this was a new array on
  // every render, which invalidated every `useMemo` downstream of it — so the
  // whole sort/flatten pipeline re-ran on each hover.
  const matchedActions = React.useMemo(
    () => (search.length > 0 ? doSearch(search) : visibleActions),
    [doSearch, search, visibleActions]
  );

  const matchedResources = React.useMemo(() => {
    const matched = search.length > 0 ? doResourceSearch(search) : allResources;
    const limit =
      search.length > 0 ? MAX_MATCHED_RESOURCES : MAX_BROWSABLE_RESOURCES;

    return matched.slice(0, limit);
  }, [allResources, doResourceSearch, search]);

  const itemDomId = (index: number) => `${listboxId}-item-${index}`;

  // A search orders by relevance; browsing orders by priority, then title.
  //
  // The alphabetical tiebreak carries real weight: only a couple of actions set
  // a priority at all, so almost every one ties at 0, and the fallback used to
  // be `getAllActions().reverse()` — registration order, backwards. That parked
  // the second action registered (Switch App Version) at the bottom of a forty
  // row list. Alphabetical at least puts a row where someone can predict it.
  const sortedActions = React.useMemo(() => {
    if (search.length > 0) {
      return matchedActions;
    }

    return [...matchedActions].sort(
      (a, b) =>
        actionPriority(b, ctx) - actionPriority(a, ctx) ||
        a.title.localeCompare(b.title)
    );
  }, [matchedActions, search.length, ctx]);

  const sortedResources = React.useMemo(() => {
    if (search.length > 0) {
      return matchedResources;
    }

    // Priority only, and the sort is stable — so within a tier the provider's
    // own order survives, which for the Recent section *is* recency. Adding the
    // alphabetical tiebreak here (as the action list does) sorted the recent
    // chats A to Z under a heading that was then nothing of the kind.
    return [...matchedResources].sort(
      (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
    );
  }, [matchedResources, search.length]);

  // Rendering order, which is also selection order — so a row's index is its
  // position here and no lookup is needed to find it.
  const allResults: (
    | { t: 'action'; action: Action }
    | { t: 'resource'; resource: ModelResource }
  )[] = React.useMemo(
    () => [
      ...sortedActions.map((action) => ({ t: 'action' as const, action })),
      ...sortedResources.map((resource) => ({
        t: 'resource' as const,
        resource,
      })),
    ],
    [sortedActions, sortedResources]
  );

  const handleSelectAction = useCallback(
    async (action: Action) => {
      analyticsClient.track('Command Selected', {
        command: action.title,
        group: action.group,
      });

      const res = await action.askOrExecute({}, context);

      if (res.isOk() && res.value.next === true) {
        return;
      }

      trackClose('action_executed');
      modalManager.close();
    },
    [analyticsClient, context, modalManager, trackClose]
  );

  // Handle keyboard navigation
  const handleSelectResultByIndex = React.useCallback(
    async (index: number) => {
      const item = allResults[index];
      if (item === undefined) {
        return;
      }

      if (item.t === 'action') {
        await handleSelectAction(item.action);
        return;
      }

      switch (item.resource.t) {
        case 'resource': {
          // The same handle actions use, so a row opens where the user is: the
          // artifact panel when one is mounted, the page otherwise. Building a
          // path here instead is what broke agents — `/agent/:id` is a legacy
          // redirect route, not the surface v1 opens an agent on.
          const resourceNavigator =
            context.getInScopeModelOfType(ResourceNavigatorModel)?.current ??
            null;

          if (resourceNavigator === null) {
            context
              .getLogger()
              .error(
                'Command palette: no ResourceNavigator in scope, cannot open resource',
                undefined,
                {
                  payload: {
                    resourceType: item.resource.resourceType,
                    resourceId: item.resource.resourceId,
                    name: item.resource.name,
                  },
                }
              );
            // Should be unreachable: every shell registers the model. Say so
            // anyway rather than leave a click that visibly did nothing.
            toast.error('Could not open that here');
            return;
          }

          resourceNavigator.open({
            resourceType: item.resource.resourceType,
            resourceId: item.resource.resourceId,
          });
          trackClose('resource_selected');
          modalManager.close();
          return;
        }
        case 'tab':
          if (navigator === null) {
            toast.error('Could not open that here');
            // Nothing above this modal provided a navigator, so the row has
            // nowhere to go. Closing anyway is indistinguishable from a
            // successful click, which is how this hid: the v1 shell scoped its
            // provider to a component that renders null, and every result row
            // silently did nothing. Report it and leave the modal open.
            context
              .getLogger()
              .error(
                'Command palette: no navigator in context, cannot open resource',
                undefined,
                { payload: { route: item.resource.tabRoute } }
              );
            return;
          }
          navigator(item.resource.tabRoute);
          trackClose('resource_selected');
          modalManager.close();
          return;
        case 'link':
          openSafeUrl(item.resource.url, '_blank');
          trackClose('resource_selected');
          modalManager.close();
          return;
        default:
          Assert.unreachable(item.resource);
      }
    },
    [
      allResults,
      context,
      handleSelectAction,
      navigator,
      modalManager,
      trackClose,
    ]
  );

  /**
   * Focus stays in the input, so the list never sees these keys and this is
   * where they have to be handled. `resolveListKey` is the list's own
   * interpretation of them, borrowed rather than restated so the two cannot
   * drift — `selectOnSpace` stays off here because in a text field a space is
   * just a space.
   */
  const handleContainerKeyDown = React.useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }

      const intent = resolveListKey(event.key, {
        itemCount: allResults.length,
        selectedIndex,
      });

      if (intent === null) {
        return;
      }

      event.preventDefault();

      switch (intent.t) {
        case 'move':
          setSelectedIndex(intent.to);
          break;
        case 'select':
          await handleSelectResultByIndex(intent.index);
          break;
        case 'escape':
          trackClose('escape');
          modalManager.close();
          break;
      }
    },
    [
      allResults.length,
      handleSelectResultByIndex,
      modalManager,
      selectedIndex,
      trackClose,
    ]
  );

  return (
    <div className="flex w-full flex-col" onKeyDown={handleContainerKeyDown}>
      <div className="relative w-full border-b border-border">
        <Icon
          icon="search"
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={search}
          data-autofocus
          autoComplete="off"
          spellCheck={false}
          aria-label="Search commands and resources"
          // Focus lives here, so this is the element that has to carry the
          // combobox contract: without `aria-activedescendant` on the *focused*
          // element a screen reader never announces the highlighted row.
          role="combobox"
          aria-expanded={allResults.length > 0}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            allResults.length > 0 ? itemDomId(selectedIndex) : undefined
          }
          placeholder="Search workspace..."
          className="h-14 rounded-none border-0 bg-transparent pl-11 pr-14 shadow-none focus-visible:border-0 focus-visible:ring-0"
          onChange={(event) => {
            const value = event.target.value;
            setSearch(value);
            setSelectedIndex(0); // Reset selection when search changes
            trackSearch(value);
          }}
        />
        {/* Escape covers this on a desktop; a phone has no Escape key and the
            modal is 92vw wide, leaving only a sliver of overlay to tap. */}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close search"
          className="absolute right-3 top-1/2 -translate-y-1/2 md:hidden"
          onClick={() => {
            trackClose('close_button');
            modalManager.close();
          }}
        >
          <Icon icon="x-mark" className="h-4 w-4" />
        </Button>
      </div>
      {allResults.length > 0 ? (
        <KeyboardNavigableList
          itemCount={allResults.length}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          onSelectItem={handleSelectResultByIndex}
          onEscape={() => {
            trackClose('escape');
            modalManager.close();
          }}
          autoFocus={false}
          focusable={false}
        >
          {({ listRef, getItemProps }) => (
            <div
              ref={listRef}
              className={classNames(
                'flex w-full flex-col gap-0.5 overflow-y-auto p-2',
                'max-h-[calc(100dvh-20rem)] md:max-h-[440px]'
              )}
              id={listboxId}
              role="listbox"
              aria-label="Search results"
              // Focus stays in the combobox input, which owns
              // `aria-activedescendant`; a tab stop here would be a dead one.
              tabIndex={-1}
            >
              <Section label="Actions" items={sortedActions}>
                {(action, index) => (
                  <Item
                    key={`action-${action.constructor.name}-${index}`}
                    icon={action.icon}
                    title={action.title}
                    trailing={
                      action.shortcut !== undefined &&
                      action.shortcut.length > 0 ? (
                        <Keys keys={action.shortcut} />
                      ) : undefined
                    }
                    isSelected={selectedIndex === index}
                    navigableProps={getItemProps(index, {
                      id: itemDomId(index),
                    })}
                  />
                )}
              </Section>
              <Section label="Recent" items={sortedResources}>
                {(resource, offset) => {
                  // Rows continue the one index space the listbox navigates.
                  const index = sortedActions.length + offset;

                  return (
                    <Item
                      key={`resource-${resource.name}-${index}`}
                      icon={resource.icon}
                      title={resource.name}
                      // Only a genuine disambiguator — the dataset a column
                      // belongs to. What kind of thing a row is ("Chat",
                      // "Agent") is carried by its icon; repeating it as a
                      // label on every row was noise.
                      trailing={
                        resource.description !== undefined ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {resource.description}
                          </span>
                        ) : undefined
                      }
                      isSelected={selectedIndex === index}
                      navigableProps={getItemProps(index, {
                        id: itemDomId(index),
                      })}
                    />
                  );
                }}
              </Section>
            </div>
          )}
        </KeyboardNavigableList>
      ) : (
        <div className="px-4 py-10 text-center">
          <Text.Caption>
            {search.length > 0 ? `No results for "${search}"` : 'No results'}
          </Text.Caption>
        </div>
      )}
    </div>
  );
};

const Keys: React.FC<{ keys: string[] }> = ({ keys }) => (
  <span className="flex items-center gap-1">
    {keys.map((key) => (
      <kbd
        key={key}
        className="flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1 font-sans text-[0.6875rem] leading-none text-muted-foreground"
      >
        {displayKey(key)}
      </kbd>
    ))}
  </span>
);

/**
 * A labelled run of rows, rendering nothing when it has none. Takes the items
 * rather than a count beside the children, so the heading cannot outlive the
 * rows it labels.
 */
function Section<T>({
  label,
  items,
  children,
}: {
  label: string;
  items: readonly T[];
  children: (item: T, index: number) => React.ReactNode;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="w-full" role="group" aria-label={label}>
      <div aria-hidden="true" className="px-3 pb-1 pt-2">
        <Text.Caption className="text-xs">{label}</Text.Caption>
      </div>
      {items.map(children)}
    </div>
  );
}

const Item: React.FC<{
  icon?: string;
  title: string;
  trailing?: React.ReactNode;
  isSelected: boolean;
  navigableProps: React.HTMLAttributes<HTMLDivElement>;
}> = ({ icon, title, trailing, isSelected, navigableProps }) => {
  const { className: navigableClassName, ...rest } = navigableProps;

  return (
    <div
      className={classNames(
        'w-full cursor-pointer rounded-md text-sm text-foreground',
        // A filled row, not a 1px border. The selected state used to be the
        // faintest thing on screen: `border-zinc-300` and no background, while
        // merely hovering a row tinted it `zinc-50`.
        isSelected ? 'bg-accent text-accent-foreground' : '',
        navigableClassName
      )}
      {...rest}
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <span className="flex min-w-0 items-center gap-2.5">
          {/* The slot is always present, even with nothing in it. A row that
              collapses to bare text reads as a section heading in a flat list,
              which is exactly how an unset (or unmapped) icon used to look. */}
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            {icon !== undefined && (
              <Icon
                icon={icon as IconName}
                className="h-4 w-4 text-muted-foreground"
              />
            )}
          </span>
          <span className="truncate">{title}</span>
        </span>
        {trailing !== undefined && (
          <span className="flex max-w-[45%] shrink-0 items-center overflow-hidden">
            {trailing}
          </span>
        )}
      </div>
    </div>
  );
};
